import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import * as d3 from 'd3';
import { ThemeService } from '@wind-log_library/fe-component-library';
import moment, { Moment } from 'moment';
import { Options } from '@angular-slider/ngx-slider';
import { round10MinLow, setMonthList } from 'app/shared/helper/date-format.helper';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { delay, skip, takeUntil } from 'rxjs/operators';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { ITimelineConfig } from 'app/shared/models/timeline.model';
import { MS_IN_10_MIN } from 'app/shared/helper/time.contants';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';

@Component({
    selector: 'app-time-range-slider',
    templateUrl: './time-range-slider.component.html',
    styleUrls: ['./time-range-slider.component.scss'],
    standalone: false
})
export class TimeRangeSliderComponent implements OnChanges, OnInit, OnDestroy {

  @Input() data;
  @Input() pin: boolean = false;
  @Input() sticky: boolean = true;
  @Input() firstMonth;
  @Input() config: ITimelineConfig;
  @Input() autoWidth: ReplaySubject<boolean>;
  @Input() coef: number = MS_IN_10_MIN
  @Input() timeSelectInput?: string;
  @Output() changeTimeRange = new EventEmitter();
  @Output() changeTimePeriod = new EventEmitter();
  @Output() toggleSticky = new EventEmitter();

  @ViewChild('sliderCanvas', { static: true }) canvasEl: ElementRef;
  @ViewChild('tref', { read: ElementRef, static: true }) tref: ElementRef;

  @HostListener('window:resize') onResize() {
    this.canvasEl.nativeElement.width = this.tref.nativeElement.offsetWidth;
    if (this.data) {
      this.drawDataLine();
      this.setSize();
    }
  }

  public minSelectedIndex: number = 0;
  public maxSelectedIndex: number;
  public options: Options;
  public buttons = ['MONTHS.ALL_PERIOD', 'MONTHS.1WEEK', 'MONTHS.1DAY', 'MONTHS.CUSTOM'];
  public showPicker: boolean;
  public selectedMonth: string;
  public months = [];
  public openDatePicker: boolean;
  public dataLoading: boolean = true;
  public startDateTs: number;
  public endDateTs: number;
  public minSelectedTs: number;
  public maxSelectedTs: number;
  public selectedMonthIndex: number = 0;
  public selectedMonthIndexRaw = 0;
  public activeButton: number = 0;

  private range: number;
  private context: CanvasRenderingContext2D;
  private size$ = new ReplaySubject();
  private dataValues: number[];
  private unsubscriber = new ReplaySubject();

  constructor(
    private themeService: ThemeService,
    private translate: TranslateService,
    private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
    private timezoneService: TimezoneService
  ) { }

  public get lastDate() {
    return this.endDateTs === round10MinLow().valueOf();
  }

  public ngOnInit() {
    this.subscribeLanguageChange();
    this.subscribeTimezoneChange();

  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.data?.currentValue) {
      this.context = (this.canvasEl.nativeElement as HTMLCanvasElement).getContext('2d');
      this.canvasEl.nativeElement.width = this.tref.nativeElement.offsetWidth;
      this.dataValues = this.data.values;
      this.setOptions();
      this.drawDataLine();
      this.dataLoading = false;
    }
    if(changes.coef?.currentValue) {
      this.setOptions();
    }

    if (changes?.firstMonth?.currentValue) {
      moment.locale(this.translate.currentLang);
      this.months = [
        this.translate.instant('ANALYZE.ROLLING_MONTH'),
        ...setMonthList(this.firstMonth)
      ];
    }

    if (changes.autoWidth) {
      this.autoWidth?.subscribe(() => {
        this.onResize()
      });
    }

    if (changes?.config?.currentValue) {
      Object.assign(this, changes.config.currentValue);
      this.selectedMonth = this.months[this.selectedMonthIndex] || this.translate.instant('MONTHS.CUSTOM_MONTH');
    }

    if (changes?.timeSelectInput?.currentValue) {
        const currentMonth = moment().startOf('month');
        const monthDifference = currentMonth.diff(changes?.timeSelectInput.currentValue, 'months');
        const index = monthDifference;
        this.selectMonth({index: index + 1, option: changes.timeSelectInput.currentValue});
    }
  }

  public ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  public rangeUserChange(event) {
    if (event.value !== this.minSelectedIndex || event.highValue !== this.maxSelectedIndex) {
      this.minSelectedIndex = event.value;
      this.maxSelectedIndex = event.highValue;
      this.showPicker = true;
      this.activeButton = 3;
      this.selectedMonthIndex = -1;
      this.minSelectedTs = this.transformDate(this.minSelectedIndex).valueOf();
      this.maxSelectedTs = this.transformDate(this.maxSelectedIndex).valueOf();
      this.setNewRange();
    }
  }

  public customUserChange(minSelectedTs, maxSelectedTs) {
    const min = this.timezoneService.applyTzToDate(minSelectedTs).valueOf();
    const max = this.timezoneService.applyTzToDate(maxSelectedTs).valueOf();

    if (min !== this.minSelectedTs || max !== this.maxSelectedTs) {
      if (min >= this.startDateTs && max <= this.endDateTs) {
        this.maxSelectedTs = max;
        this.minSelectedTs = min;
        this.minSelectedIndex = (this.minSelectedTs - this.startDateTs) / this.coef;
        this.maxSelectedIndex = (this.maxSelectedTs - this.startDateTs) / this.coef;
        this.setNewRange();
      } else {
        this.selectedMonthIndex = -1;
        this.selectedMonth = this.translate.instant('MONTHS.CUSTOM_MONTH')
        const minTs = moment(max).subtract(1, 'month').valueOf();
        this.changeTimePeriod.emit({ 
          minTs,
          maxTs: max,
          minSelectedTs:min,
          selectedMonthIndex: -1,
          activeButton: 3
        });
      }
    }
  }

  public setCustomRange({minSelectedTs, maxSelectedTs}) {
    const min = this.timezoneService.applyTzToDate(minSelectedTs).valueOf();
    const max = this.timezoneService.applyTzToDate(maxSelectedTs).valueOf();

    if (min !== this.minSelectedTs || max !== this.maxSelectedTs) {
      if (min >= this.startDateTs && max <= this.endDateTs) {
        this.maxSelectedTs = max;
        this.minSelectedTs = min;
        this.minSelectedIndex = (this.minSelectedTs - this.startDateTs) / this.coef;
        this.maxSelectedIndex = (this.maxSelectedTs - this.startDateTs) / this.coef;
        this.setNewRange();
      } else {
        this.selectedMonthIndex = -1;
        this.selectedMonth = this.translate.instant('MONTHS.CUSTOM_MONTH')
        const minTs = moment(max).subtract(1, 'month').valueOf();
        this.changeTimePeriod.emit({ 
          minTs,
          maxTs: max,
          minSelectedTs:min,
          selectedMonthIndex: -1,
          activeButton: 3
        });
      }
    }
  }

  public setRange(index,showPicker = true) {
    this.activeButton = index;
    if (index === 3) {
      this.showPicker = showPicker;
      this.selectedMonthIndex = -1;
      this.openDatePicker = true;
    } else {
      this.showPicker = false;
      this.openDatePicker = false;
      this.minSelectedTs = index === 1 ? this.setWeek().min : index === 2 ? this.setDay().min : this.startDateTs;
      this.maxSelectedTs = index === 1 ? this.setWeek().max : index === 2 ? this.setDay().max : this.endDateTs;
      this.minSelectedIndex = (this.minSelectedTs - this.startDateTs) / this.coef;
      this.maxSelectedIndex = (this.maxSelectedTs - this.startDateTs) / this.coef;
      if(index === 0) {
        this.selectedMonthIndex = this.selectedMonthIndexRaw;
      }
      this.selectedMonth = this.months[this.selectedMonthIndex];
      this.selectedMonth ??= this.translate.instant('MONTHS.CUSTOM_MONTH');
      this.setNewRange();
    }
  }

  public selectMonth({index, option}) {
    this.showPicker = false;
    this.selectedMonth = option;
    this.activeButton = 0;
    this.selectedMonthIndexRaw = index;
    if (this.selectedMonthIndex === 0 && index === 1) {
      this.previous();
    } else if (this.selectedMonthIndex === 1 && index === 0) {
      this.next();
    } else if (index > 1) {
      this.selectedMonthIndex = index;
      this.setNewPeriod();
    } else {
      this.selectedMonthIndex = index;
      this.minSelectedTs = this.selectedMonthIndex ? moment().startOf('month').valueOf() : round10MinLow().subtract(1, 'month').valueOf();
      this.maxSelectedTs = round10MinLow().valueOf();
      this.minSelectedIndex = (this.minSelectedTs - round10MinLow().subtract(1, 'month').valueOf()) / this.coef;
      const { minSelectedTs } = this;
      this.changeTimePeriod.emit({
        minTs: round10MinLow().subtract(1, 'month').valueOf(),
        maxTs: this.maxSelectedTs,
        minSelectedTs,
        selectedMonthIndex: index,
        activeButton: 0
      });
    }
  }

  public next() {
    this.showPicker = false;
    if (this.selectedMonthIndex === -1) {
      this.nextCustom();
    } else if (this.selectedMonthIndex !== 0) {
      this.activeButton = 0;
      this.selectedMonthIndex--;
      this.selectedMonth = this.months[this.selectedMonthIndex];
      this.selectedMonthIndex ? this.setNewPeriod() : this.startCurentMonth();
    }
  }

  public previous() {
    this.showPicker = false;
    if (this.selectedMonthIndex === -1) {
      this.prevCustom();
    } else if (this.selectedMonthIndex !== (this.months.length - 1)) {
      this.activeButton = 0;
      this.selectedMonthIndex++;
      this.selectedMonth = this.months[this.selectedMonthIndex];
      this.selectedMonthIndex !== 1 ? this.setNewPeriod() : this.startCurentMonth();
    }
  }

  public prevCustom() {
    this.minSelectedTs = moment(this.minSelectedTs).startOf('month').valueOf();
    const now = round10MinLow().valueOf();
    const monthEnd = moment(this.minSelectedTs).endOf('month').valueOf();
    this.maxSelectedTs = monthEnd > now ? now : monthEnd;
    this.minSelectedTs = this.timezoneService.applyTzToDate(this.minSelectedTs).valueOf();
    this.maxSelectedTs = this.timezoneService.applyTzToDate(this.maxSelectedTs).valueOf();
    this.maxSelectedTs = monthEnd > now ? now : this.maxSelectedTs;
    this.setNewPeriod();
  }

  public nextCustom() {
    this.minSelectedTs = moment(this.maxSelectedTs).startOf('month').valueOf();
    const now = round10MinLow().valueOf();
    const monthEnd = moment(this.maxSelectedTs).endOf('month').valueOf();
    this.maxSelectedTs = monthEnd > now ? now : monthEnd;
    this.setNewPeriod();
  }

  public pinToggle() {
    this.toggleSticky.emit();
  }

  private startCurentMonth() {
    this.minSelectedTs = this.selectedMonthIndex ? moment().startOf('month').valueOf() : this.startDateTs;
    this.maxSelectedTs = this.endDateTs;
    this.minSelectedIndex = (this.minSelectedTs - this.startDateTs) / this.coef;
    this.maxSelectedIndex = Math.round((this.maxSelectedTs - this.startDateTs) / this.coef);
    this.setNewRange();
  }

  private getEndOfMonth(ts: number | Moment): number {
    return moment(ts).startOf('month').add(1, 'month').valueOf();
  }

  private setNewPeriod() {
    this.dataLoading = true;
    if ([0, 1].includes(this.selectedMonthIndex)) {
      this.minSelectedTs = this.selectedMonthIndex ?
        this.timezoneService.applyTzToDate(moment().startOf('month').valueOf()).valueOf() 
        : round10MinLow().subtract(1, 'month').valueOf();
      this.maxSelectedTs = round10MinLow().valueOf();
    } else if(this.selectedMonthIndex > 1) {
      this.minSelectedTs = this.timezoneService.applyTzToDate(moment(this.selectedMonth, 'MMMM YYYY').startOf('month').valueOf()).valueOf();
      this.maxSelectedTs = this.timezoneService.applyTzToDate(this.getEndOfMonth(moment(this.selectedMonth, 'MMMM YYYY'))).valueOf();

    } else {
      this.selectedMonth = moment.tz(this.minSelectedTs,this.timezoneService.selectedOption).format('MMMM YYYY');
      this.selectedMonthIndex = this.months.indexOf(this.selectedMonth);
      this.activeButton = 0;
    }
    const minTs = this.selectedMonthIndex === 1 ? round10MinLow().subtract(1, 'month').valueOf() : this.minSelectedTs;
    this.maxSelectedIndex = Math.round((this.maxSelectedTs - this.startDateTs) / this.coef);
    const { minSelectedTs, selectedMonthIndex, activeButton } = this;
    this.changeTimePeriod.emit({ 
      minTs, 
      maxTs: this.maxSelectedTs, 
      minSelectedTs,
      selectedMonthIndex,
      activeButton
    });
  }

  private setNewRange() {
    /* Temporary fix for summer time change due to receiving less points */
    this.changeTimeRange.emit({
      minIndex: Math.floor(this.minSelectedIndex),
      maxIndex: Math.floor(this.maxSelectedIndex),
      min: this.minSelectedTs,
      max: this.maxSelectedTs,
      selectedMonthIndex: this.selectedMonthIndex,
      activeButton: this.activeButton
    });
  }

  private setDay() {
    const min = this.transformDate(Math.round((this.endDateTs-this.startDateTs)/this.coef)).subtract(24, 'hours').valueOf();
    const max = this.transformDate(Math.round((this.endDateTs-this.startDateTs)/this.coef)).valueOf();
    return { min, max };
  }

  private setWeek() {
    const min = this.transformDate(Math.round((this.endDateTs-this.startDateTs)/this.coef)).subtract(7, 'days').valueOf();
    const max = this.transformDate(Math.round((this.endDateTs-this.startDateTs)/this.coef)).valueOf();
    return { min, max };
  }

  private drawDataLine() {
    this.context.clearRect(0, 0, 9999, 100);
    let yAmin = 0;
    let yAmax = Math.max(...this.dataValues) * 1.1;

    const xAxis = d3.scaleLinear()
      .domain([0, this.dataValues.length])
      .range([0, this.tref.nativeElement.offsetWidth]);

    const yAxis = d3.scaleLinear()
      .domain([yAmin, yAmax])
      .range([50, 0]);

    this.context.beginPath();
    this.context.lineJoin = 'round';
    this.context.moveTo(xAxis(0), yAxis(this.dataValues[0]));
    for (let i = 0; i <= this.dataValues.length; i=i+1) {
      this.context.lineTo(xAxis(i), yAxis(this.dataValues[i]));
    }
    this.context.strokeStyle = this.themeService.getColor('--primary-background');
    this.context.lineWidth = 1;
    this.context.stroke();
  }

  private setSize(): void {
    this.size$.next({
      width: this.tref.nativeElement.offsetWidth,
      height: this.tref.nativeElement.offsetHeight
    });
  }

  private setOptions() {
    this.range = this.endDateTs - this.startDateTs;
    this.minSelectedTs = this.minSelectedTs || this.startDateTs + this.minSelectedIndex * this.coef;
    this.maxSelectedTs = this.maxSelectedTs || this.endDateTs;
    this.minSelectedIndex = (this.minSelectedTs - this.startDateTs) / this.coef;
    this.maxSelectedIndex = (this.maxSelectedTs - this.startDateTs) / this.coef;
    this.options = {
      ceil: Math.round(this.range / this.coef),
      draggableRange: true,
      floor: 0,
      minRange: 0,
      step: 1,
      translate: (value: number): string => {
        return this.dateTimeFormatPreferencePipe.transform(this.transformDate(value).toDate(), 'dmt', this.timezoneService.selectedOption)
      }
    };
  }

  public tz(ts: number) {
    return ts ? this.timezoneService.unApplyTzFromDate(ts).valueOf() : ts;
  }

  private transformDate(value = 0): Moment {
    return moment(this.startDateTs).add(this.coef * value, 'milliseconds');
  }

  private subscribeLanguageChange() {
    this.translate.onLangChange.pipe(
      takeUntil(this.unsubscriber)
    ).subscribe((event: LangChangeEvent) => {
      if (event) {
        moment.locale(this.translate.currentLang);
        this.months = [this.translate.instant('ANALYZE.ROLLING_MONTH'), ...setMonthList(this.firstMonth)];
        this.selectedMonth = this.months[this.selectedMonthIndex];
      }
    });
  }

  private subscribeTimezoneChange(): void {
    this.timezoneService.getTimezoneObservable(true).pipe(takeUntil(this.unsubscriber),  skip(1),  delay(350))
    .subscribe(() => {
        if (this.selectedMonthIndex > 0) {
          this.setNewPeriod();
        }
        if (this.activeButton) {
          this.setRange(this.activeButton,false);
        } 
      });
  }

}
